import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://whiskyandco.com.ar";

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  return element;
};

const ensureLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  return element;
};

const toAbsoluteUrl = (value, pathname) => {
  const fallbackPath = pathname || "/";

  if (!value) {
    return `${SITE_URL}${fallbackPath}`;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalized = value.startsWith("/") ? value : `/${value}`;
  return `${SITE_URL}${normalized}`;
};

const SEO = ({
  title,
  description,
  canonicalPath,
  type = "website",
  image,
  noindex = false,
  structuredData,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      const descriptionMeta = ensureMeta('meta[name="description"]', { name: "description" });
      descriptionMeta.setAttribute("content", description);
    }

    const canonicalHref = toAbsoluteUrl(canonicalPath, location.pathname);
    const canonicalLink = ensureLink('link[rel="canonical"]', { rel: "canonical" });
    canonicalLink.setAttribute("href", canonicalHref);

    const robotsMeta = ensureMeta('meta[name="robots"]', { name: "robots" });
    robotsMeta.setAttribute("content", noindex ? "noindex, nofollow" : "index, follow");

    const ogTitle = ensureMeta('meta[property="og:title"]', { property: "og:title" });
    ogTitle.setAttribute("content", title || document.title);

    const ogDescription = ensureMeta('meta[property="og:description"]', { property: "og:description" });
    ogDescription.setAttribute("content", description || "");

    const ogType = ensureMeta('meta[property="og:type"]', { property: "og:type" });
    ogType.setAttribute("content", type);

    const ogUrl = ensureMeta('meta[property="og:url"]', { property: "og:url" });
    ogUrl.setAttribute("content", canonicalHref);

    if (image) {
      const absoluteImage = toAbsoluteUrl(image, location.pathname);
      const ogImage = ensureMeta('meta[property="og:image"]', { property: "og:image" });
      ogImage.setAttribute("content", absoluteImage);

      const twitterCard = ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" });
      twitterCard.setAttribute("content", "summary_large_image");

      const twitterImage = ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" });
      twitterImage.setAttribute("content", absoluteImage);
    } else {
      const ogImage = document.head.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.remove();
      }

      const twitterImage = document.head.querySelector('meta[name="twitter:image"]');
      if (twitterImage) {
        twitterImage.remove();
      }

      const twitterCard = ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" });
      twitterCard.setAttribute("content", "summary");
    }

    const twitterTitle = ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" });
    twitterTitle.setAttribute("content", title || document.title);

    const twitterDescription = ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" });
    twitterDescription.setAttribute("content", description || "");

    const existingJsonLd = document.head.querySelector("script[data-seo-json-ld='true']");
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    if (structuredData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-json-ld", "true");
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [
    canonicalPath,
    description,
    image,
    location.pathname,
    noindex,
    structuredData,
    title,
    type,
  ]);

  return null;
};

export default SEO;